import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { POLLING_DELAY_MS } from '@cast/constants';
import { apiClient } from '@cast/infrastructure-api';
import { useAssetListClustersQuery } from '@cast/shared-cluster-context-data-access';
import { useOrganizations } from '@cast/shared-organization-context-data-access';
import { CloudIntegrationSyncJob } from '@cast/types-inventory';
import { makeIsEnabled } from '@cast/utils';

import {
  CLOUD_ASSET_INTEGRATIONS_QUERY_KEY,
  useCloudAssetIntegrationsQuery,
} from '@oneclick/hooks/queries/inventory';

const COMMITMENT_ASSET_TYPES = new Set([
  'RESERVED_INSTANCES',
  'SAVINGS_PLANS',
  'GCP_RESOURCE_CUDS',
  'GCP_FLEX_CUDS',
  'AZURE_RESERVATIONS',
  'AZURE_SAVINGS_PLANS',
  'AWS_CAPACITY_RESERVATIONS',
  'GCP_CAPACITY_RESERVATIONS',
]);

const sumSyncedAssets = (
  statuses: CloudIntegrationSyncJob[] | undefined,
  assetTypes: Set<string>
): number | undefined => {
  if (!statuses?.length) {
    return undefined;
  }

  const total = statuses
    .filter((status) => assetTypes.has(status.assetType ?? ''))
    .reduce((sum, status) => sum + (status.syncedAssets ?? 0), 0);

  return total > 0 ? total : undefined;
};

type UseDiscoveredEnvironmentStatsArgs = {
  polling: boolean;
  enabled?: boolean;
  baselineIntegrationIds: Set<string>;
};

export const useDiscoveredEnvironmentStats = ({
  polling,
  enabled = true,
  baselineIntegrationIds,
}: UseDiscoveredEnvironmentStatsArgs) => {
  const { currentOrganization } = useOrganizations();
  const organizationId = currentOrganization?.id;

  const { assetListClusters, isLoading: isClustersLoading } =
    useAssetListClustersQuery({
      polling,
      enabled: makeIsEnabled(!!organizationId, enabled),
    });

  const { data: integrations, isLoading: isIntegrationsLoading } =
    useCloudAssetIntegrationsQuery({
      enabled: enabled && polling,
      refetchInterval: polling ? POLLING_DELAY_MS : false,
    });

  const newIntegration = (integrations ?? []).find(
    (integration) =>
      Boolean(integration.id) &&
      !baselineIntegrationIds.has(integration.id ?? '')
  );
  const integrationId = newIntegration?.id;

  const { data: syncStatuses, isLoading: isSyncReportLoading } = useQuery({
    queryKey: [
      CLOUD_ASSET_INTEGRATIONS_QUERY_KEY,
      organizationId,
      integrationId,
    ],
    queryFn: async () => {
      const { data: syncReport } =
        await apiClient.inventory.getCloudAssetIntegrationSyncReport({
          organizationId: organizationId!,
          cloudAssetIntegrationId: integrationId!,
        });

      return syncReport.statuses ?? [];
    },
    enabled: makeIsEnabled(
      !!organizationId && !!integrationId,
      enabled && polling
    ),
    refetchInterval: polling ? POLLING_DELAY_MS : false,
  });

  const stats = useMemo(
    () => ({
      clusters: assetListClusters?.length,
      virtualMachines: sumSyncedAssets(syncStatuses, new Set(['INSTANCE'])),
      volume: sumSyncedAssets(syncStatuses, new Set(['DISK'])),
      loadBalancers: undefined as number | undefined,
      commitments: sumSyncedAssets(syncStatuses, COMMITMENT_ASSET_TYPES),
    }),
    [assetListClusters, syncStatuses]
  );

  return {
    stats,
    assetListClusters,
    newIntegration,
    syncStatuses,
    isLoading:
      isClustersLoading || isIntegrationsLoading || isSyncReportLoading,
  };
};
